import { useEditorState } from "@tiptap/react";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Undo2, Redo2, Baseline, Highlighter, Type, Eraser } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FONTS, PALETTE, SIZES } from "@/lib/richtext";

const Btn = ({ on, onClick, title, icon: Icon, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} aria-pressed={!!on} data-testid={`rte-${testId}`}
    className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${on ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:text-slate-100 hover:bg-veil/[0.05]"}`}>
    <Icon className="w-4 h-4" />
  </button>
);

const Swatches = ({ title, icon: Icon, current, onPick, onClear, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" onMouseDown={(e) => e.preventDefault()} title={title} data-testid={`rte-${testId}`}
        className="h-8 w-8 rounded-md flex flex-col items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-veil/[0.05] transition-colors">
        <Icon className="w-4 h-4" /><span className="w-4 h-0.5 rounded-full mt-0.5" style={{ background: current || "#475569" }} />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-52 p-2 bg-panel border-line" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
      <div className="grid grid-cols-6 gap-1.5">
        {PALETTE.map(([name, hex]) => (
          <button key={hex} type="button" title={name} onClick={() => onPick(hex)} data-testid={`rte-${testId}-${name.toLowerCase()}`}
            className={`h-6 w-6 rounded-full ring-offset-2 ring-offset-panel transition-transform hover:scale-110 ${current === hex ? "ring-2 ring-slate-200" : ""}`} style={{ background: hex }} />
        ))}
      </div>
      <button type="button" onClick={onClear} className="mt-2 w-full flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100 px-1 py-1" data-testid={`rte-${testId}-clear`}><Eraser className="w-3 h-3" /> Clear</button>
    </PopoverContent>
  </Popover>
);

const selectCls = "h-8 rounded-md bg-canvas border border-line text-xs text-slate-300 px-1.5";

export const RichToolbar = ({ editor, extra }) => {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"),
      h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }), ul: e.isActive("bulletList"), ol: e.isActive("orderedList"), quote: e.isActive("blockquote"),
      font: e.getAttributes("textStyle").fontFamily?.replace(/["']/g, "") || "", size: e.getAttributes("textStyle").fontSize || "",
      color: e.getAttributes("textStyle").color || "", mark: e.getAttributes("highlight").color || "",
    }),
  });
  const c = () => editor.chain().focus();
  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-line bg-inset sticky top-16 z-10 rounded-t-xl" data-testid="rte-toolbar">
      <select value={s.font} onChange={(e) => (e.target.value ? c().setFontFamily(e.target.value).run() : c().unsetFontFamily().run())} className={`${selectCls} w-36`} title="Font" data-testid="rte-font">
        {FONTS.map(([l, v]) => <option key={l} value={v} style={{ fontFamily: v || undefined }}>{l}</option>)}
      </select>
      <div className="flex items-center gap-0.5 pl-1" title="Text size">
        <Type className="w-3.5 h-3.5 text-slate-500" />
        <select value={s.size} onChange={(e) => (e.target.value ? c().setFontSize(e.target.value).run() : c().unsetFontSize().run())} className={`${selectCls} w-20`} data-testid="rte-size">
          {SIZES.map(([l, v]) => <option key={l} value={v}>{l}</option>)}
        </select>
      </div>
      <span className="w-px h-5 bg-line mx-1" />
      <Btn on={s.bold} onClick={() => c().toggleBold().run()} title="Bold" icon={Bold} testId="bold" />
      <Btn on={s.italic} onClick={() => c().toggleItalic().run()} title="Italic" icon={Italic} testId="italic" />
      <Btn on={s.underline} onClick={() => c().toggleUnderline().run()} title="Underline" icon={Underline} testId="underline" />
      <Btn on={s.strike} onClick={() => c().toggleStrike().run()} title="Strikethrough" icon={Strikethrough} testId="strike" />
      <Swatches title="Text colour" icon={Baseline} current={s.color} onPick={(h) => c().setColor(h).run()} onClear={() => c().unsetColor().run()} testId="color" />
      <Swatches title="Highlight" icon={Highlighter} current={s.mark} onPick={(h) => c().setHighlight({ color: h }).run()} onClear={() => c().unsetHighlight().run()} testId="highlight" />
      <span className="w-px h-5 bg-line mx-1" />
      <Btn on={s.h2} onClick={() => c().toggleHeading({ level: 2 }).run()} title="Heading" icon={Heading2} testId="h2" />
      <Btn on={s.h3} onClick={() => c().toggleHeading({ level: 3 }).run()} title="Subheading" icon={Heading3} testId="h3" />
      <Btn on={s.ul} onClick={() => c().toggleBulletList().run()} title="Bulleted list" icon={List} testId="ul" />
      <Btn on={s.ol} onClick={() => c().toggleOrderedList().run()} title="Numbered list" icon={ListOrdered} testId="ol" />
      <Btn on={s.quote} onClick={() => c().toggleBlockquote().run()} title="Quote" icon={Quote} testId="quote" />
      <span className="w-px h-5 bg-line mx-1" />
      <Btn onClick={() => c().undo().run()} title="Undo" icon={Undo2} testId="undo" />
      <Btn onClick={() => c().redo().run()} title="Redo" icon={Redo2} testId="redo" />
      <div className="ml-auto pr-1">{extra}</div>
    </div>
  );
};
